import { useTranslator } from '@Localization';
import { observer } from 'mobx-react-lite';
import { ReactElement, useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ConfirmationModalWithTextBox } from '../../../components/ConfirmationModalWithTextBox';
import { ACDLError } from '../../../models/error.model';
import { useAttachmentsService } from '../attachments.service';

export const AttachmentConfirmDeleteModalContainer = observer((): ReactElement => {
  const attachmentsService = useAttachmentsService();
  const { t } = useTranslator();
  const { attachmentId } = useParams<{ attachmentId: string }>();
  const [attachmentsCount, setAttachmentsCount] = useState<number | undefined>(undefined);

  const toggleModal = (): void => {
    attachmentsService.toggleDeleteAttachmentModal();
    setAttachmentsCount(undefined);
  };

  const onConfirmAsync = async (): Promise<void> => {
    const store = attachmentsService.store;
    const parentId = attachmentId;

    await attachmentsService.deleteAttachmentsAsync(store.selectedAttachments.isAllSelected, store.selectedAttachments.ids, parentId);

    setAttachmentsCount(undefined);
  };

  const getAttachmentsCount = async (): Promise<void> => {
    if (attachmentsCount !== undefined) {
      return;
    }

    const store = attachmentsService.store;
    const result = await attachmentsService.getSelectedAttachments(store.selectedAttachments.isAllSelected, store.selectedAttachments.ids, attachmentId);

    if (!(result instanceof ACDLError)) {
      setAttachmentsCount(result);
    }
  };

  useEffect(() => {
    if (attachmentsService.store.showDeleteConfirmationModal) {
      void getAttachmentsCount();
    }
  }, [attachmentsService.store.showDeleteConfirmationModal]);

  const question = (
    <Trans
      i18nKey='delete_attachment_confirmation_multi'
      count={attachmentsCount}
      values={{ count: attachmentsCount }}
      components={{ b: <b /> }}
    />
  );

  return (
    <ConfirmationModalWithTextBox
      confirmationHeader={t('delete_attachments_header')}
      confirmationQuestion={t('delete_attachment_text_multi', { count: attachmentsCount ?? null })}
      confirmationTextHeader={question}
      confirmationText={t('delete_attachment_confirmation', { count: attachmentsCount ?? null })}
      onCancel={toggleModal}
      onConfirm={onConfirmAsync}
      showModal={attachmentsService.store.showDeleteConfirmationModal}
      showSpinner={attachmentsService.store.isDeleteRequestInProgress}
      confirmationYes={t('delete')}
    />
  );
});
